import { useEffect, useState } from "react";
import { Check, Copy, Info, Loader2 } from "lucide-react";

import { api } from "@/api/client";

/**
 * Which build is running.
 *
 * The sidebar already carries this, in eleven-point grey at the very bottom -
 * findable once you know it is there and easy to miss otherwise. Settings is
 * where someone actually goes looking for a version number, so it is here too,
 * legibly, with the commit that a bug report needs.
 *
 * Read from the server rather than compiled in: after a deploy the browser may
 * still be running the previous bundle from cache, so a baked-in version would
 * confidently report the old one at exactly the moment someone is checking
 * whether the new one landed.
 */
export default function AboutSection() {
  const [build, setBuild] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .health()
      .then(setBuild)
      .catch((err) => setError(err.message || "Could not read the version."));
  }, []);

  const copy = async () => {
    const summary =
      `EDDM Organizer v${build.version} (${build.commit}${build.dirty ? "+" : ""})` +
      (build.commitDate ? ` — ${build.commitDate}` : "");
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard access can be refused; the text is on screen either way.
    }
  };

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card-title">
        <Info size={20} />
        Version
      </h2>

      {error && <div className="alert alert-error">{error}</div>}

      {!build ? (
        <div className="muted">
          <Loader2 size={16} className="spin" /> Reading...
        </div>
      ) : (
        <>
          <div className="build-facts">
            <div>
              <span className="form-label">Version</span>
              <strong className="build-value">v{build.version}</strong>
            </div>
            <div>
              <span className="form-label">Build</span>
              <strong className="build-value build-mono">
                {build.commit}
                {build.dirty ? "+" : ""}
              </strong>
            </div>
            <div>
              <span className="form-label">Dated</span>
              <strong className="build-value">
                {build.commitDate
                  ? new Date(build.commitDate).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "unknown"}
              </strong>
            </div>
          </div>

          {build.dirty && (
            <div className="alert alert-warning" style={{ marginTop: "1rem" }}>
              <strong>This build carries uncommitted changes.</strong> It matches
              no recorded commit, so treat it as temporary.
            </div>
          )}

          <div className="button-row" style={{ marginTop: "1.25rem" }}>
            <button className="btn btn-outline btn-sm" onClick={copy}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy for a bug report"}
            </button>
          </div>

          <p className="field-hint" style={{ marginTop: "0.75rem" }}>
            The version comes from the server, so it is what is actually
            deployed rather than what this browser happens to have cached.
          </p>
        </>
      )}
    </div>
  );
}
